import React, { Fragment, PureComponent } from 'react';
import { Flex } from 'app/components/common/Flex';
import { Button, Col, Divider, notification, Popconfirm, Row, Select } from 'antd';
import AuthorisationUtils from 'app/common/context/AuthorisationUtils';
import { SolutionState, UpdateCoordinationStateRequest } from 'app/model/SOL49';
import { ServiceFactory } from 'app/service/ServiceFactory';
import 'app/components/panels/coordination/CoordinationStatusPanel.less';
import { ReloadOutlined } from '@ant-design/icons';
import Utils from '../../../common/util/Utils';
import { CoordinationUtils } from '../../../common/util/CoordinationUtils';
import { CoordinationStatusType } from '../../../model/client/SOL49';

interface CoordinationStatusProps {
    coordinationId: string;
    onStatusChange: (status: CoordinationStatusType) => void;
    selectedFmp: string;
    selectedSolution: string;
    disabled: boolean;
}

interface CoordinationStatusState {
    status: CoordinationStatusType;
    localStatus: CoordinationStatusType;
}

export class CoordinationStatusPanel extends PureComponent<CoordinationStatusProps, CoordinationStatusState> {
    private interval: NodeJS.Timer;

    constructor(props) {
        super(props);

        this.state = {
            status: undefined,
            localStatus: undefined,
        };
    }

    componentDidMount() {
        this.refresh();
        // Refresh every 15s
        this.interval = setInterval(() => this.refresh(), 5000);
    }

    componentWillUnmount() {
        clearInterval(this.interval);
    }

    refresh = () => {
        ServiceFactory.sol49Service()
            .getCoordinationStatus(this.props.coordinationId)
            .then(reply => {
                // Check if state has been updated
                if (
                    Utils.isNotExists(this.state.status) ||
                    Object.entries(reply.data).some(([fmp, solState]) => {
                        return Object.entries(solState).some(([sol, state]) => {
                            return state !== this.state.status[fmp][sol];
                        });
                    })
                ) {
                    this.setState({ status: reply.data }, () => {
                        this.updateAckStatus();
                        this.props.onStatusChange(reply.data);
                    });
                }
            })
            .catch(err => notification.error({ message: 'Error retrieving coordination', description: err }));
    };

    private updateAckStatus = () => {
        const status = this.state.status;
        if (CoordinationUtils.isNotAcknowledged(status)) {
            // Update state to ack and open panel
            const fmpSolutionStatus = status[AuthorisationUtils.getFmpUnit()];
            Object.keys(fmpSolutionStatus)
                .filter(solName => {
                    return fmpSolutionStatus[solName] === SolutionState.NO_RESPONSE;
                })
                .forEach(solName => {
                    ServiceFactory.sol49Service()
                        .updateCoordinationStatus({
                            anuId: AuthorisationUtils.getFmpUnit(),
                            id: this.props.coordinationId,
                            solutionName: solName,
                            state: SolutionState.ACKNOWLEDGED,
                        })
                        .then(reply => {
                            this.setState({ status: reply.data }, () => this.props.onStatusChange(reply.data));
                        })
                        .catch(err => console.log(err));
                });
        }
    };

    getSelectClassName = (fmp: string, solution: string, displayedState: SolutionState) => {
        let className = '';

        if (displayedState === SolutionState.APPROVED) {
            className = 'approved';
        } else if (displayedState === SolutionState.REJECTED) {
            className = 'rejected';
        }

        // When connected as FMP, highlight its column
        // When connected as NMOC, highlight the selected FMP column
        const sameFMP = AuthorisationUtils.isFmpUser()
            ? fmp === AuthorisationUtils.getFmpUnit()
            : fmp === this.props.selectedFmp;
        if (solution === this.props.selectedSolution && sameFMP) {
            className += ' highlighted';
        }

        const status = this.state.status[fmp][solution];
        if (displayedState !== status) {
            className += ' local';
        }

        return className;
    };

    private updateState(fmp: string, solName: string, newState: SolutionState) {
        const newStatus = Object.assign({}, this.state.localStatus);
        if (!newStatus[fmp]) {
            newStatus[fmp] = {};
        }
        newStatus[fmp][solName] = newState;
        this.setState({
            localStatus: newStatus,
        });
    }

    private sendStateUpdate = (fmp: string) => {
        Object.entries(this.state.localStatus[fmp]).forEach(([solName, newState]) => {
            if (newState !== this.state.status[fmp][solName]) {
                this.sendSolutionStateUpdate(fmp, solName, newState);
            }
        });
    };

    private sendSolutionStateUpdate = (fmp: string, solName: string, newState: SolutionState) => {
        const request: UpdateCoordinationStateRequest = {
            id: this.props.coordinationId,
            anuId: fmp,
            solutionName: solName,
            state: newState,
        };
        ServiceFactory.sol49Service()
            .updateCoordinationStatus(request)
            .then(reply => {
                // Init local status
                const newStatus = Object.assign({}, this.state.localStatus);
                newStatus[fmp] = undefined;
                this.setState(
                    {
                        status: reply.data,
                        localStatus: newStatus,
                    },
                    () => this.props.onStatusChange(reply.data),
                );
            })
            .catch(err => notification.error({ message: 'Error updating state', description: err }));
    };

    private getDisplayedState = (fmp: string, solName: string): SolutionState =>
        this.state.localStatus?.[fmp]?.[solName] || this.state.status[fmp][solName];

    private hasUpdates = () => Object.keys(this.state.status).some(fmp => this.hasUpdatedFMPState(fmp));

    private hasUpdatedFMPState = (fmp: string) =>
        Utils.isExists(this.state.localStatus?.[fmp]) &&
        Object.entries(this.state.status[fmp]).some(([sol, state]) => state !== this.state.localStatus[fmp]?.[sol]);

    private hasSetAllSolutions = (fmp: string) =>
        Object.keys(this.state.status[fmp]).every(
            sol => this.getDisplayedState(fmp, sol) !== SolutionState.ACKNOWLEDGED,
        );

    private reset = () => this.setState({ localStatus: undefined });

    render() {
        if (!this.state.status) return;

        const solutionsNames: string[] = Object.keys(Object.values(this.state.status)[0]);
        const fmpList = Object.keys(this.state.status);

        return (
            <Flex
                className={`coordination-status ${this.props.disabled ? 'disabled' : ''}`}
                direction="vertical"
                grow={false}>
                <Flex size="2em" className="header">
                    <span>STATUS</span>
                    <Button style={{ marginLeft: '1.5em' }} onClick={this.reset} disabled={!this.hasUpdates()}>
                        <ReloadOutlined />
                    </Button>
                </Flex>
                <Flex direction="vertical" grow={false} className="overflow status">
                    <Row wrap={false}>
                        <Col span={4}></Col>
                        <Col span={1} className="divider-container">
                            <Divider type="vertical" className="divider" />
                        </Col>
                        {solutionsNames.map(solName => {
                            return (
                                <Fragment key={solName}>
                                    <Col span={7}>{solName}</Col>
                                    <Col span={1} className="divider-container">
                                        <Divider type="vertical" className="divider" />
                                    </Col>
                                </Fragment>
                            );
                        })}
                    </Row>
                    <Divider className="horizontal-divider"></Divider>
                    {fmpList.map((fmp, i) => {
                        const sendButtonDisabled =
                            (AuthorisationUtils.isFmpUser() && fmp !== AuthorisationUtils.getFmpUnit()) ||
                            !this.hasUpdatedFMPState(fmp) ||
                            !this.hasSetAllSolutions(fmp);
                        return (
                            <Fragment key={fmp + i}>
                                <Row wrap={false}>
                                    <Col span={4} style={{ alignSelf: 'center', textAlign: 'center' }}>
                                        {fmp}
                                    </Col>
                                    <Col span={1} className="divider-container">
                                        <Divider type="vertical" className="divider" />
                                    </Col>
                                    {solutionsNames.map((sol, idx) => {
                                        const displayedState = this.getDisplayedState(fmp, sol);
                                        return (
                                            <Fragment key={sol + idx}>
                                                <Col span={7}>
                                                    <Select
                                                        value={displayedState}
                                                        onChange={state => this.updateState(fmp, sol, state)}
                                                        disabled={
                                                            AuthorisationUtils.isFmpUser() &&
                                                            fmp !== AuthorisationUtils.getFmpUnit()
                                                        }
                                                        className={this.getSelectClassName(fmp, sol, displayedState)}>
                                                        <Select.Option value={SolutionState.ACKNOWLEDGED}>
                                                            {SolutionState.ACKNOWLEDGED}
                                                        </Select.Option>
                                                        <Select.Option value={SolutionState.APPROVED}>
                                                            {SolutionState.APPROVED}
                                                        </Select.Option>
                                                        <Select.Option value={SolutionState.REJECTED}>
                                                            {SolutionState.REJECTED}
                                                        </Select.Option>
                                                    </Select>
                                                </Col>
                                                <Col span={1} className="divider-container">
                                                    <Divider type="vertical" className="divider" />
                                                </Col>
                                            </Fragment>
                                        );
                                    })}
                                    <div style={{ alignSelf: 'center' }}>
                                        <Popconfirm
                                            title={'Send status update ?'}
                                            okText={'Yes'}
                                            cancelText={'No'}
                                            onConfirm={() => this.sendStateUpdate(fmp)}
                                            disabled={sendButtonDisabled}>
                                            <Button disabled={sendButtonDisabled}>SEND</Button>
                                        </Popconfirm>
                                    </div>
                                </Row>
                                <Divider className="horizontal-divider"></Divider>
                            </Fragment>
                        );
                    })}
                </Flex>
            </Flex>
        );
    }
}
